import React from 'react';
import { Box, Card, CardContent, Container } from '@mui/material';
import AttendLogo from '../assets/attendlogo.png';

interface AuthShellProps {
  children: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md';
}

export const authTextFieldSx = {
  '& .MuiOutlinedInput-root': {
    minHeight: 48,
    backgroundColor: 'var(--an-surface)',
    borderRadius: 'var(--an-radius-sm)',
    transition: 'border-color var(--an-transition), background-color var(--an-transition)',
    '& fieldset': {
      borderColor: 'var(--an-border)',
    },
    '&:hover fieldset': {
      borderColor: 'rgb(var(--an-primary-rgb) / 0.42)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'var(--an-primary)',
      borderWidth: 2,
    },
    '&.Mui-error fieldset': {
      borderColor: 'var(--an-error)',
    },
  },
  '& .MuiInputAdornment-root svg': {
    color: 'var(--an-text-tertiary)',
  },
  '& .MuiInputBase-input': {
    color: 'var(--an-text-primary)',
  },
  '& .MuiInputLabel-root': {
    color: 'var(--an-text-secondary)',
    fontWeight: 700,
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: 'var(--an-primary)',
    backgroundColor: 'var(--an-surface)',
    paddingInline: '0.25rem',
  },
};

export const authAlertSx = {
  mb: 3,
  fontWeight: 700,
  borderRadius: 'var(--an-radius-sm)',
  '& .MuiAlert-message': {
    fontWeight: 700,
  },
  '& .MuiAlert-icon': {
    alignItems: 'center',
  },
};

export const authButtonSx = {
  py: 1.35,
  borderRadius: 'var(--an-radius-sm)',
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: 800,
  color: 'var(--an-white)',
  backgroundColor: 'var(--an-primary)',
  '&:hover': {
    backgroundColor: 'var(--an-primary-dark)',
  },
  '&:disabled': {
    color: 'var(--an-text-tertiary)',
    backgroundColor: 'var(--an-border-light)',
  },
};

const AuthShell: React.FC<AuthShellProps> = ({ children, maxWidth = 'sm' }) => (
  <Box
    sx={{
      minHeight: '100dvh',
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', lg: 'minmax(420px, 0.92fr) minmax(480px, 1.08fr)' },
      backgroundColor: 'var(--an-background)',
    }}
  >
    <Box
      sx={{
        display: { xs: 'none', lg: 'flex' },
        minHeight: '100dvh',
        position: 'relative',
        overflow: 'hidden',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 2,
        p: { lg: 3.5, xl: 5 },
        color: 'var(--an-sidebar-text)',
        background:
          'radial-gradient(circle at 18% 16%, rgb(var(--an-accent-rgb) / 0.22), var(--an-transparent) 24rem), linear-gradient(180deg, var(--an-sidebar-bg), var(--an-primary-dark))',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgb(var(--an-sidebar-text-rgb) / 0.055) 1px, var(--an-transparent) 1px), linear-gradient(90deg, rgb(var(--an-sidebar-text-rgb) / 0.055) 1px, var(--an-transparent) 1px)',
          backgroundSize: '40px 40px',
          maskImage: 'linear-gradient(to bottom, rgb(var(--an-sidebar-text-rgb) / 0.72), var(--an-transparent))',
          pointerEvents: 'none',
        },
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: 2,
            py: 1.25,
            borderRadius: 'var(--an-radius-md)',
            backgroundColor: 'rgb(var(--an-surface-rgb) / 0.94)',
            border: '1px solid rgb(var(--an-surface-rgb) / 0.64)',
            boxShadow: 'var(--an-shadow-sm)',
          }}
        >
          <Box component="img" src={AttendLogo} alt="AttendNow" sx={{ height: 40, width: 'auto', display: 'block' }} />
        </Box>
      </Box>

      <Box aria-hidden="true" sx={{ position: 'relative', zIndex: 1, display: 'grid', gap: { lg: 2, xl: 3 }, minHeight: 0 }}>
        <Box sx={{ maxWidth: 520 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 1.25,
              py: 0.75,
              mb: 2.25,
              borderRadius: 'var(--an-radius-sm)',
              color: 'var(--an-accent)',
              backgroundColor: 'rgb(var(--an-accent-rgb) / 0.10)',
              border: '1px solid rgb(var(--an-accent-rgb) / 0.22)',
              fontSize: '0.78rem',
              fontWeight: 800,
              letterSpacing: 0,
            }}
          >
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: 'var(--an-accent)' }} />
            AI workforce operations
          </Box>
          <Box sx={{ width: '82%', height: 20, mb: 1.35, borderRadius: 99, backgroundColor: 'rgb(var(--an-sidebar-text-rgb) / 0.92)' }} />
          <Box sx={{ width: '62%', height: 20, mb: 2, borderRadius: 99, backgroundColor: 'rgb(var(--an-sidebar-text-rgb) / 0.76)' }} />
          <Box sx={{ display: 'grid', gap: 0.8 }}>
            {[0, 1].map((item) => (
              <Box
                key={item}
                sx={{
                  width: item === 0 ? '72%' : '56%',
                  height: 9,
                  borderRadius: 99,
                  backgroundColor: 'rgb(var(--an-sidebar-text-rgb) / 0.30)',
                }}
              />
            ))}
          </Box>
        </Box>

        <Box sx={{ position: 'relative', height: 'clamp(300px, 50vh, 430px)', minHeight: 0 }}>
          <Box
            sx={{
              position: 'absolute',
              inset: 'auto -3rem -3rem auto',
              width: 260,
              height: 260,
              borderRadius: '50%',
              border: '1px solid rgb(var(--an-sidebar-text-rgb) / 0.12)',
              background: 'rgb(var(--an-surface-rgb) / 0.025)',
            }}
          />
          <Box
            sx={{
              position: 'relative',
              width: 'min(100%, 600px)',
              borderRadius: 'var(--an-radius-md)',
              border: '1px solid rgb(var(--an-surface-rgb) / 0.18)',
              background: 'linear-gradient(145deg, rgb(var(--an-surface-rgb) / 0.18), rgb(var(--an-surface-rgb) / 0.07))',
              boxShadow: 'var(--an-shadow-sm)',
              backdropFilter: 'blur(22px)',
              WebkitBackdropFilter: 'blur(22px)',
              p: 1.15,
            }}
          >
            <Box
              sx={{
                overflow: 'hidden',
                borderRadius: 'calc(var(--an-radius-lg) - 5px)',
                backgroundColor: 'rgb(var(--an-surface-rgb) / 0.96)',
                border: '1px solid rgb(var(--an-surface-rgb) / 0.62)',
              }}
            >
              <Box
                sx={{
                  height: 54,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  borderBottom: '1px solid var(--an-border-light)',
                  backgroundColor: 'var(--an-surface)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 30, height: 30, borderRadius: 'var(--an-radius-sm)', backgroundColor: 'var(--an-primary)' }} />
                  <Box>
                    <Box sx={{ width: 116, height: 10, mb: 0.8, borderRadius: 99, backgroundColor: 'var(--an-text-primary)' }} />
                    <Box sx={{ width: 160, height: 7, borderRadius: 99, backgroundColor: 'var(--an-border)' }} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Box sx={{ width: 74, height: 30, borderRadius: 'var(--an-radius-sm)', backgroundColor: 'var(--an-surface-muted)', border: '1px solid var(--an-border-light)' }} />
                  <Box sx={{ width: 84, height: 30, borderRadius: 'var(--an-radius-sm)', backgroundColor: 'var(--an-primary)' }} />
                </Box>
              </Box>

              <Box sx={{ p: { lg: 1.35, xl: 2 }, display: 'grid', gap: { lg: 1, xl: 1.5 } }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
                  {[0, 1, 2, 3].map((item) => (
                    <Box
                      key={item}
                      sx={{
                        minHeight: { lg: 62, xl: 82 },
                        p: 1.2,
                        borderRadius: 'var(--an-radius-md)',
                        backgroundColor: item === 0 ? 'var(--an-primary-light)' : 'var(--an-surface)',
                        border: '1px solid var(--an-border-light)',
                        boxShadow: 'var(--an-shadow-sm)',
                      }}
                    >
                      <Box sx={{ width: '46%', height: 7, mb: 1.35, borderRadius: 99, backgroundColor: item === 0 ? 'rgb(var(--an-primary-rgb) / 0.24)' : 'var(--an-border)' }} />
                      <Box sx={{ width: item === 2 ? '50%' : '68%', height: 24, borderRadius: 'var(--an-radius-sm)', backgroundColor: item === 0 ? 'var(--an-primary)' : item === 1 ? 'var(--an-accent)' : 'var(--an-neutral-light)' }} />
                    </Box>
                  ))}
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1.4fr 0.75fr', gap: 1.25 }}>
                  <Box
                    sx={{
                      height: { lg: 120, xl: 180 },
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: 'var(--an-radius-md)',
                      backgroundColor: 'var(--an-surface)',
                      border: '1px solid var(--an-border-light)',
                      boxShadow: 'var(--an-shadow-sm)',
                    }}
                  >
                    {[0, 1, 2, 3, 4].map((item) => (
                      <Box key={item} sx={{ position: 'absolute', left: 16, right: 16, bottom: 24 + item * 28, height: 1, backgroundColor: 'var(--an-border-light)' }} />
                    ))}
                    {[0, 1, 2, 3, 4, 5, 6].map((item) => (
                      <Box
                        key={item}
                        sx={{
                          position: 'absolute',
                          left: `${9 + item * 12}%`,
                          bottom: 24,
                          width: 14,
                          height: `${30 + ((item * 17) % 62)}%`,
                          borderRadius: '999px 999px 0 0',
                          backgroundColor: item === 5 ? 'var(--an-accent)' : 'rgb(var(--an-primary-rgb) / 0.34)',
                        }}
                      />
                    ))}
                  </Box>

                  <Box
                    sx={{
                      height: { lg: 120, xl: 180 },
                      p: { lg: 1, xl: 1.4 },
                      display: 'grid',
                      gap: 1,
                      borderRadius: 'var(--an-radius-md)',
                      backgroundColor: 'var(--an-surface)',
                      border: '1px solid var(--an-border-light)',
                      boxShadow: 'var(--an-shadow-sm)',
                    }}
                  >
                    {[0, 1, 2, 3, 4].map((item) => (
                      <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: item === 0 ? 'var(--an-success)' : item === 1 ? 'var(--an-warning)' : 'var(--an-border)' }} />
                        <Box sx={{ flex: 1, height: 7, borderRadius: 99, backgroundColor: 'var(--an-border-light)' }} />
                        <Box sx={{ width: `${26 + item * 8}%`, height: 7, borderRadius: 99, backgroundColor: item === 0 ? 'var(--an-success-light)' : 'var(--an-primary-light)' }} />
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              position: 'absolute',
              right: 18,
              top: { lg: 24, xl: 42 },
              width: 182,
              p: 1.45,
              borderRadius: 'var(--an-radius-md)',
              backgroundColor: 'rgb(var(--an-surface-rgb) / 0.16)',
              border: '1px solid rgb(var(--an-surface-rgb) / 0.18)',
              boxShadow: 'var(--an-shadow-sm)',
              backdropFilter: 'blur(18px)',
              WebkitBackdropFilter: 'blur(18px)',
            }}
          >
            <Box sx={{ width: '44%', height: 8, mb: 1.25, borderRadius: 99, backgroundColor: 'rgb(var(--an-surface-rgb) / 0.34)' }} />
            <Box sx={{ width: '72%', height: 26, borderRadius: 'var(--an-radius-sm)', backgroundColor: 'rgb(var(--an-accent-rgb) / 0.38)' }} />
          </Box>
        </Box>

        <Box sx={{ height: 1, flexShrink: 0, background: 'rgb(var(--an-sidebar-text-rgb) / 0.16)' }} />
      </Box>
    </Box>

    <Box
      sx={{
        minHeight: '100dvh',
        position: 'relative',
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 2, sm: 4, lg: 7 },
        py: { xs: 4, lg: 6 },
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at 80% 12%, rgb(var(--an-primary-rgb) / 0.08), var(--an-transparent) 24rem), radial-gradient(circle at 12% 88%, rgb(var(--an-accent-rgb) / 0.08), var(--an-transparent) 22rem)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth={maxWidth} sx={{ position: 'relative', zIndex: 1 }}>
        <Card
          elevation={0}
          sx={{
            width: '100%',
            borderRadius: 'var(--an-radius-md)',
            overflow: 'hidden',
            border: '1px solid var(--an-border-light)',
            boxShadow: 'var(--an-shadow-sm)',
            backgroundColor: 'var(--an-surface)',
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4.5 } }}>{children}</CardContent>
        </Card>
      </Container>
    </Box>
  </Box>
);

export default AuthShell;


